import cn from 'classnames';
import { type KeyboardEvent, useRef } from 'react';
import { IconTooltip } from 'sonata-react-components';

import type { ToolSubGroupTab } from '../../ActionToolsPanel.types';

import './attributionTabs.scss';

type Props = {
  activeTab?: string;
  onChangeTab?: (id: string) => void;
  tabs: ToolSubGroupTab[];
};

/** Pill-shaped segmented control; the info icon is a sibling of the tab button (no nested interactive). */
export function AttributionTabs({ activeTab, onChangeTab, tabs }: Props) {
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const handleKeyDown = (event: KeyboardEvent, index: number) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) {
      return;
    }
    event.preventDefault();
    const next = tabs[(index + step + tabs.length) % tabs.length];
    onChangeTab?.(next.id);
    tabRefs.current[next.id]?.focus();
  };

  return (
    <div className="attributionTabs" role="tablist">
      {tabs.map((tab, index) => {
        const selected = tab.id === activeTab;
        return (
          <div className={cn('attributionTabs__tab', { 'attributionTabs__tab--selected': selected })} key={tab.id}>
            <button
              aria-selected={selected}
              className="attributionTabs__button"
              data-testid={`attribution-tab-${tab.id}`}
              onClick={() => onChangeTab?.(tab.id)}
              onKeyDown={event => handleKeyDown(event, index)}
              ref={element => {
                tabRefs.current[tab.id] = element;
              }}
              role="tab"
              tabIndex={selected ? 0 : -1}
              type="button"
            >
              {tab.title}
            </button>
            {!!tab.tooltip && (
              <IconTooltip effect="solid" event="mouseenter" icon="fa-sharp fa-light fa-circle-info" place="top">
                <div className="actionToolsPanel__tooltipWrapper">{tab.tooltip}</div>
              </IconTooltip>
            )}
          </div>
        );
      })}
    </div>
  );
}
